import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Form,
  FormFeedback,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Nav,
  NavItem,
  NavLink,
} from "reactstrap";
import { PermisoDto, RolFormState } from "../../../types/ajustes";
import classNames from "classnames";

interface RolModalProps {
  isOpen: boolean;
  toggle: () => void;
  formState: RolFormState;
  onChange: (field: keyof RolFormState, value: RolFormState[keyof RolFormState]) => void;
  onSubmit: () => void;
  saving: boolean;
  permisos: PermisoDto[];
  errors?: Partial<Record<keyof RolFormState, string>>;
}

const PERMISOS_EXTRA: PermisoDto[] = [
  {
    id: -1001,
    codigo: "ajustes.acceder",
    descripcion: "Acceder al módulo de ajustes",
    categoria: "ajustes",
  },
  {
    id: -1002,
    codigo: "modulos.rendicion_caja",
    descripcion: "Acceder al módulo de Rendición de Caja",
    categoria: "modulos",
  },
  {
    id: -1003,
    codigo: "modulos.conciliacion_caja",
    descripcion: "Acceder al módulo de Conciliación de Caja",
    categoria: "modulos",
  },
  {
    id: -1101,
    codigo: "informes.contabilidad",
    descripcion: "Ver informes contables",
    categoria: "informes",
  },
  {
    id: -1102,
    codigo: "informes.informatica",
    descripcion: "Ver informes de informática",
    categoria: "informes",
  },
  {
    id: -1103,
    codigo: "informes.logistica",
    descripcion: "Ver informes de logística",
    categoria: "informes",
  },
  {
    id: -1104,
    codigo: "informes.produccion",
    descripcion: "Ver informes de producción",
    categoria: "informes",
  },
  {
    id: -1105,
    codigo: "informes.rrhh",
    descripcion: "Ver informes de RRHH",
    categoria: "informes",
  },
  {
    id: -1106,
    codigo: "informes.stock_compras",
    descripcion: "Ver informes de stock de compras",
    categoria: "informes",
  },
  {
    id: -1107,
    codigo: "informes.stock_inventarios",
    descripcion: "Ver informes de stock de inventarios",
    categoria: "informes",
  },
  {
    id: -1108,
    codigo: "informes.stock_transferencias",
    descripcion: "Ver informes de stock de transferencias",
    categoria: "informes",
  },
  {
    id: -1109,
    codigo: "informes.tesoreria",
    descripcion: "Ver informes de tesorería",
    categoria: "informes",
  },
  {
    id: -1110,
    codigo: "informes.finanzas",
    descripcion: "Ver informes financieros",
    categoria: "informes",
  },
  {
    id: -1111,
    codigo: "informes.gerencia",
    descripcion: "Ver informes gerenciales",
    categoria: "informes",
  },
  {
    id: -1112,
    codigo: "informes.ventas_planilla",
    descripcion: "Ver informes de ventas",
    categoria: "informes",
  },
  {
    id: -1113,
    codigo: "informes.colecciones.ver",
    descripcion: "Ver colecciones de artículos",
    categoria: "informes",
  },
  {
    id: -1114,
    codigo: "informes.colecciones.editar",
    descripcion: "Crear y editar colecciones de artículos",
    categoria: "informes",
  },
  {
    id: -1115,
    codigo: "informes.colecciones.clonar",
    descripcion: "Clonar colecciones de otros usuarios",
    categoria: "informes",
  },
  {
    id: -1116,
    codigo: "informes.agente_ia",
    descripcion: "Usar el agente de IA en informes",
    categoria: "informes",
  },
  {
    id: -1201,
    codigo: "informes.item.compras_online",
    descripcion: "Compras (online)",
    categoria: "informes",
  },
  {
    id: -1202,
    codigo: "informes.item.transferencias_online",
    descripcion: "Transferencias (online)",
    categoria: "informes",
  },
  {
    id: -1203,
    codigo: "informes.item.rrhh_online",
    descripcion: "RRHH (online)",
    categoria: "informes",
  },
  {
    id: -1204,
    codigo: "informes.item.tesoreria_online",
    descripcion: "Tesoreria (online)",
    categoria: "informes",
  },
  {
    id: -1205,
    codigo: "informes.item.contabilidad_online",
    descripcion: "Contabilidad (online)",
    categoria: "informes",
  },
  {
    id: -1206,
    codigo: "informes.item.logistica_online",
    descripcion: "Logistica (online)",
    categoria: "informes",
  },
  {
    id: -1207,
    codigo: "informes.item.produccion_online",
    descripcion: "Produccion (online)",
    categoria: "informes",
  },
  {
    id: -1208,
    codigo: "informes.item.gerencia_online",
    descripcion: "Gerencia (online)",
    categoria: "informes",
  },
  {
    id: -1209,
    codigo: "informes.item.informatica_online",
    descripcion: "Informatica (online)",
    categoria: "informes",
  },
  {
    id: -1210,
    codigo: "informes.item.colecciones_online",
    descripcion: "Colecciones de articulos",
    categoria: "informes",
  },
  {
    id: -1211,
    codigo: "informes.item.gestion_colecciones_online",
    descripcion: "Gestion de colecciones de articulos",
    categoria: "informes",
  },
  {
    id: -1212,
    codigo: "informes.item.clonado_colecciones_online",
    descripcion: "Clonado de colecciones entre usuarios",
    categoria: "informes",
  },
  {
    id: -1213,
    codigo: "informes.item.agente_ia_online",
    descripcion: "Agente IA de informes",
    categoria: "informes",
  },
  {
    id: -1214,
    codigo: "informes.item.inventarios_online",
    descripcion: "Inventarios (online)",
    categoria: "informes",
  },
  {
    id: -1215,
    codigo: "informes.item.control_stock_online",
    descripcion: "Control de stock (online)",
    categoria: "informes",
  },
  {
    id: -1216,
    codigo: "informes.item.control_stock_offline",
    descripcion: "Control de stock (offline)",
    categoria: "informes",
  },
  {
    id: -1217,
    codigo: "informes.item.recetas_offline",
    descripcion: "Recetas (offline)",
    categoria: "informes",
  },
  {
    id: -1218,
    codigo: "informes.item.costos_mp_offline",
    descripcion: "Costos MP (offline)",
    categoria: "informes",
  },
  {
    id: -1219,
    codigo: "informes.item.reposicion_stock_offline",
    descripcion: "Reposicion de stock (offline)",
    categoria: "informes",
  },
  {
    id: -1220,
    codigo: "informes.item.consumo_receta_offline",
    descripcion: "Consumo/receta (offline)",
    categoria: "informes",
  },
  {
    id: -1221,
    codigo: "informes.item.planilla_caja_online",
    descripcion: "Planilla Caja (online)",
    categoria: "informes",
  },
  {
    id: -1222,
    codigo: "informes.item.vs_anho_anterior_offline",
    descripcion: "Vs ano anterior (offline)",
    categoria: "informes",
  },
  {
    id: -1223,
    codigo: "informes.item.cartera_vencida_online",
    descripcion: "Cartera vencida (online)",
    categoria: "informes",
  },
  {
    id: -1224,
    codigo: "informes.item.cartera_vencida_offline",
    descripcion: "Cartera vencida (offline)",
    categoria: "informes",
  },
];

const CONTROL_CAJA_SUBGRUPOS: { titulo: string; codigos: string[] }[] = [
  {
    titulo: "Operaciones de carga",
    codigos: [
      "controlcaja.guardar_boletas",
      "controlcaja.guardar_gastos",
      "controlcaja.configurar_notificaciones",
      "controlcaja.confirmar_rendicion",
      "controlcaja.guardar_formas_pago",
      "controlcaja.guardar_fondo",
      "controlcaja.crear_rendicion",
    ],
  },
  {
    titulo: "Listar cajas",
    codigos: [
      "controlcaja.listar",
      "controlcaja.listar_hoy",
      "controlcaja.listar_dia_anterior",
      "controlcaja.listar_mes",
      "controlcaja.listar_45_dias",
      "controlcaja.listar_semestre",
      "controlcaja.listar_todo",
    ],
  },
  {
    titulo: "Consultas de cajas y filtros",
    codigos: [
      "controlcaja.ver_estados",
      "controlcaja.ver_filtros",
      "controlcaja.ver_reporte",
      "controlcaja.ver_formas_pago",
      "controlcaja.ver_sucursales",
    ],
  },
];

type InformePadreHijo = {
  padre: string;
  hijos: string[];
};

const INFORMES_SUBGRUPOS: { titulo: string; nodos: InformePadreHijo[] }[] = [
  {
    titulo: "En tiempo real",
    nodos: [
      { padre: "informes.stock_compras", hijos: ["informes.item.compras_online"] },
      { padre: "informes.stock_transferencias", hijos: ["informes.item.transferencias_online"] },
      { padre: "informes.rrhh", hijos: ["informes.item.rrhh_online"] },
      { padre: "informes.tesoreria", hijos: ["informes.item.tesoreria_online"] },
      { padre: "informes.contabilidad", hijos: ["informes.item.contabilidad_online"] },
      { padre: "informes.logistica", hijos: ["informes.item.logistica_online"] },
      { padre: "informes.produccion", hijos: ["informes.item.produccion_online"] },
      { padre: "informes.gerencia", hijos: ["informes.item.gerencia_online"] },
      { padre: "informes.informatica", hijos: ["informes.item.informatica_online"] },
      { padre: "informes.colecciones.ver", hijos: ["informes.item.colecciones_online"] },
      {
        padre: "informes.colecciones.editar",
        hijos: ["informes.item.gestion_colecciones_online"],
      },
      {
        padre: "informes.colecciones.clonar",
        hijos: ["informes.item.clonado_colecciones_online"],
      },
      { padre: "informes.agente_ia", hijos: ["informes.item.agente_ia_online"] },
    ],
  },
  {
    titulo: "Datos consolidados",
    nodos: [
      {
        padre: "informes.stock_inventarios",
        hijos: [
          "informes.item.inventarios_online",
          "informes.item.control_stock_online",
          "informes.item.control_stock_offline",
          "informes.item.recetas_offline",
          "informes.item.costos_mp_offline",
          "informes.item.reposicion_stock_offline",
          "informes.item.consumo_receta_offline",
        ],
      },
      {
        padre: "informes.ventas_planilla",
        hijos: [
          "informes.item.planilla_caja_online",
          "informes.item.vs_anho_anterior_offline",
        ],
      },
      {
        padre: "informes.finanzas",
        hijos: [
          "informes.item.cartera_vencida_online",
          "informes.item.cartera_vencida_offline",
        ],
      },
    ],
  },
];

const INFORMES_HIJOS_POR_PADRE: Record<string, string[]> = INFORMES_SUBGRUPOS.reduce(
  (acc, subgrupo) => {
    subgrupo.nodos.forEach((nodo) => {
      acc[nodo.padre] = nodo.hijos;
    });
    return acc;
  },
  {} as Record<string, string[]>,
);

const INFORMES_PADRE_POR_HIJO: Record<string, string> = Object.entries(
  INFORMES_HIJOS_POR_PADRE,
).reduce(
  (acc, [padre, hijos]) => {
    hijos.forEach((hijo) => {
      acc[hijo] = padre;
    });
    return acc;
  },
  {} as Record<string, string>,
);

const RolModal = ({
  isOpen,
  toggle,
  formState,
  onChange,
  onSubmit,
  saving,
  permisos,
  errors = {},
}: RolModalProps) => {
  const handleInput =
    (field: keyof RolFormState) =>
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const target = event.target;
      const value =
        target instanceof HTMLInputElement && target.type === "checkbox"
          ? target.checked
          : target.value;
      onChange(field, value as never);
    };

  const [grupoActivo, setGrupoActivo] = useState<string | null>(null);
  const [busquedaPermiso, setBusquedaPermiso] = useState("");

  const formatCategoria = (categoria: string) => {
    if (!categoria) return "";
    return categoria.charAt(0).toUpperCase() + categoria.slice(1).toLowerCase();
  };

  const permisosExtendidos = useMemo(() => {
    const existentes = new Set(permisos.map((permiso) => permiso.codigo));
    const lista = [...permisos];
    PERMISOS_EXTRA.forEach((permiso) => {
      if (!existentes.has(permiso.codigo)) {
        lista.push(permiso);
      }
    });
    return lista;
  }, [permisos]);

  const gruposPermisos = useMemo(() => {
    const mapa = new Map<string, PermisoDto[]>();
    permisosExtendidos.forEach((permiso) => {
      const categoria = permiso.categoria?.trim();
      const nombreGrupo = categoria && categoria.length > 0 ? categoria : "General";
      const lista = mapa.get(nombreGrupo) ?? [];
      lista.push(permiso);
      mapa.set(nombreGrupo, lista);
    });
    return Array.from(mapa.entries())
      .map(([categoria, lista]) => ({
        categoria,
        permisos: lista.sort((a, b) =>
          (a.descripcion || a.codigo).localeCompare(b.descripcion || b.codigo)
        ),
      }))
      .sort((a, b) => a.categoria.localeCompare(b.categoria));
  }, [permisosExtendidos]);

  useEffect(() => {
    if (gruposPermisos.length === 0) {
      if (grupoActivo !== null) {
        setGrupoActivo(null);
      }
      return;
    }
    const existe = gruposPermisos.some((grupo) => grupo.categoria === grupoActivo);
    if (!existe) {
      setGrupoActivo(gruposPermisos[0].categoria);
    }
  }, [grupoActivo, gruposPermisos]);

  const togglePermiso =
    (codigo: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const checked = event.target.checked;
      const seleccionados = new Set(formState.permisos);
      if (checked) seleccionados.add(codigo);
      else seleccionados.delete(codigo);

      const hijos = INFORMES_HIJOS_POR_PADRE[codigo];
      if (hijos) {
        if (!checked) {
          hijos.forEach((hijo) => seleccionados.delete(hijo));
        } else if (hijos.length === 1) {
          seleccionados.add(hijos[0]);
        }
      }

      const padre = INFORMES_PADRE_POR_HIJO[codigo];
      if (padre) {
        const hermanos = INFORMES_HIJOS_POR_PADRE[padre] ?? [];
        if (checked) {
          seleccionados.add(padre);
        } else {
          const quedaAlgunHermanoSeleccionado = hermanos.some((hijo) =>
            seleccionados.has(hijo),
          );
          if (!quedaAlgunHermanoSeleccionado) {
            seleccionados.delete(padre);
          }
        }
      }
      onChange("permisos", Array.from(seleccionados));
    };

  const grupoVisible =
    grupoActivo && gruposPermisos.length > 0
      ? gruposPermisos.find((grupo) => grupo.categoria === grupoActivo)
      : gruposPermisos[0];

  const normalizarBusqueda = (texto: string) => texto.toLowerCase().trim();
  const coincideBusqueda = (permiso: PermisoDto) => {
    const termino = normalizarBusqueda(busquedaPermiso || "");
    if (!termino) {
      return true;
    }
    const objetivo = `${permiso.descripcion ?? ""} ${permiso.codigo}`.toLowerCase();
    return objetivo.includes(termino);
  };

  const renderPermisoCheckbox = (
    permiso: PermisoDto | undefined,
    options?: { compact?: boolean },
  ) => {
    if (!permiso) {
      return null;
    }
    const checked = formState.permisos.includes(permiso.codigo);
    const inputId = `permiso-${permiso.id}`;
    const compact = Boolean(options?.compact);
    return (
      <div className={compact ? "col-12 col-md-6" : "col-12 col-md-4"} key={permiso.id}>
        <div className="form-check mb-2">
          <Input id={inputId} type="checkbox" checked={checked} onChange={togglePermiso(permiso.codigo)} />
          <Label className="form-check-label" htmlFor={inputId}>
            {permiso.descripcion || permiso.codigo}
          </Label>
        </div>
      </div>
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      centered
      className="rol-modal"
      contentClassName="rol-modal-content"
      modalClassName="rol-modal-wrapper"
    >
      <ModalHeader toggle={toggle}>Rol</ModalHeader>
      <ModalBody>
        <Form>
          <FormGroup>
            <Label htmlFor="rol-nombre">Nombre</Label>
            <Input
              id="rol-nombre"
              value={formState.nombre}
              onChange={handleInput("nombre")}
              invalid={Boolean(errors.nombre)}
            />
            {errors.nombre && <FormFeedback>{errors.nombre}</FormFeedback>}
          </FormGroup>
          <FormGroup>
            <Label htmlFor="rol-descripcion">Descripción</Label>
            <Input
              id="rol-descripcion"
              type="textarea"
              value={formState.descripcion}
              onChange={handleInput("descripcion")}
            />
          </FormGroup>
          <FormGroup>
            <Label>Permisos</Label>
            <Input
              placeholder="Buscar permiso (por nombre o código)..."
              className="mb-3"
              value={busquedaPermiso}
              onChange={(event) => setBusquedaPermiso(event.target.value)}
            />
            {permisosExtendidos.length === 0 ? (
              <p className="text-muted mb-0">No hay permisos disponibles.</p>
            ) : (
              <>
                {gruposPermisos.length > 1 && (
                  <Nav pills className="arrow-navtabs nav-success mb-3">
                    {gruposPermisos.map((grupo) => (
                      <NavItem key={grupo.categoria}>
                        <NavLink
                          href="#"
                          className={classNames({ active: grupoActivo === grupo.categoria })}
                          onClick={(event) => {
                            event.preventDefault();
                            setGrupoActivo(grupo.categoria);
                          }}
                        >
                          {formatCategoria(grupo.categoria)}
                        </NavLink>
                      </NavItem>
                    ))}
                  </Nav>
                )}
                {grupoVisible && (
                  <div className="permiso-grupo rounded border p-3">
                    {(() => {
                      const permisosFiltrados = grupoVisible.permisos.filter(coincideBusqueda);
                      if (permisosFiltrados.length === 0) {
                        return <p className="text-muted mb-0">No hay permisos que coincidan con la búsqueda.</p>;
                      }
                      const aplicarSeleccionGrupo = (seleccionar: boolean) => {
                        const seleccionados = new Set(formState.permisos);
                        permisosFiltrados.forEach((permiso) => {
                          if (seleccionar) {
                            seleccionados.add(permiso.codigo);
                          } else {
                            seleccionados.delete(permiso.codigo);
                          }
                        });
                        onChange("permisos", Array.from(seleccionados));
                      };
                      const categoriaGrupo = grupoVisible.categoria?.toLowerCase() ?? "";
                      const esControlCaja = categoriaGrupo === "control de caja";
                      const esInformes = categoriaGrupo === "informes";
                      return (
                        <>
                          <div className="d-flex justify-content-end gap-2 mb-3">
                            <Button size="sm" color="light" onClick={() => aplicarSeleccionGrupo(false)}>
                              Quitar todos
                            </Button>
                            <Button size="sm" color="primary" onClick={() => aplicarSeleccionGrupo(true)}>
                              Marcar todos
                            </Button>
                          </div>
                          {esControlCaja ? (
                            <>
                              {CONTROL_CAJA_SUBGRUPOS.map((subgrupo) => {
                                const permisosSubgrupo = subgrupo.codigos
                                  .map((codigo) =>
                                    permisosFiltrados.find((permiso) => permiso.codigo === codigo),
                                  )
                                  .filter(Boolean);
                                if (permisosSubgrupo.length === 0) {
                                  return null;
                                }
                                return (
                                  <div key={subgrupo.titulo} className="mb-3">
                                    <h6 className="text-uppercase text-muted fs-13 mb-2">{subgrupo.titulo}</h6>
                                    <div className="row">
                                      {permisosSubgrupo.map((permiso) => renderPermisoCheckbox(permiso))}
                                    </div>
                                  </div>
                                );
                              })}
                              <div className="row">
                                {permisosFiltrados
                                  .filter(
                                    (permiso) =>
                                      !CONTROL_CAJA_SUBGRUPOS.some((subgrupo) =>
                                        subgrupo.codigos.includes(permiso.codigo),
                                      ),
                                  )
                                  .map((permiso) => renderPermisoCheckbox(permiso))}
                              </div>
                            </>
                          ) : esInformes ? (
                            <>
                              {INFORMES_SUBGRUPOS.map((subgrupo) => {
                                const permisosByCode = new Map(
                                  grupoVisible.permisos.map((permiso) => [
                                    permiso.codigo,
                                    permiso,
                                  ]),
                                );
                                const nodosVisibles = subgrupo.nodos
                                  .map((nodo) => {
                                    const padre = permisosByCode.get(nodo.padre);
                                    if (!padre) return null;
                                    const hijos = nodo.hijos
                                      .map((codigoHijo) => permisosByCode.get(codigoHijo))
                                      .filter((permiso): permiso is PermisoDto => Boolean(permiso));
                                    const padreCoincide = coincideBusqueda(padre);
                                    const hijosCoinciden = hijos.filter((hijo) =>
                                      coincideBusqueda(hijo),
                                    );
                                    if (!padreCoincide && hijosCoinciden.length === 0) {
                                      return null;
                                    }
                                    return {
                                      padre,
                                      hijos: padreCoincide ? hijos : hijosCoinciden,
                                    };
                                  })
                                  .filter(
                                    (
                                      nodo,
                                    ): nodo is {
                                      padre: PermisoDto;
                                      hijos: PermisoDto[];
                                    } => Boolean(nodo),
                                  );

                                if (nodosVisibles.length === 0) {
                                  return null;
                                }
                                return (
                                  <div key={subgrupo.titulo} className="mb-3">
                                    <h6 className="text-uppercase text-muted fs-13 mb-2">{subgrupo.titulo}</h6>
                                    {nodosVisibles.map((nodo) => (
                                      <div key={nodo.padre.codigo} className="mb-2 pb-2 border-bottom">
                                        <div className="row">
                                          {renderPermisoCheckbox(nodo.padre)}
                                        </div>
                                        {nodo.hijos.length > 0 ? (
                                          <div className="row ps-md-3">
                                            {nodo.hijos.map((hijo) =>
                                              renderPermisoCheckbox(hijo, { compact: true }),
                                            )}
                                          </div>
                                        ) : null}
                                      </div>
                                    ))}
                                  </div>
                                );
                              })}
                              <div className="row">
                                {permisosFiltrados
                                  .filter(
                                    (permiso) =>
                                      !INFORMES_SUBGRUPOS.some((subgrupo) =>
                                        subgrupo.nodos.some(
                                          (nodo) =>
                                            nodo.padre === permiso.codigo ||
                                            nodo.hijos.includes(permiso.codigo),
                                        ),
                                      ),
                                  )
                                  .map((permiso) => renderPermisoCheckbox(permiso))}
                              </div>
                            </>
                          ) : (
                            <div className="row">
                              {permisosFiltrados.map((permiso) => renderPermisoCheckbox(permiso))}
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                )}
              </>
            )}
          </FormGroup>
          <FormGroup check>
            <Input
              id="rol-activo"
              type="checkbox"
              checked={formState.activo}
              onChange={handleInput("activo")}
            />
            <Label check htmlFor="rol-activo">
              Activo
            </Label>
          </FormGroup>
        </Form>
      </ModalBody>
      <ModalFooter>
        <Button color="light" onClick={toggle} disabled={saving}>
          Cancelar
        </Button>
        <Button color="primary" onClick={onSubmit} disabled={saving}>
          {saving ? "Guardando..." : "Guardar"}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default RolModal;
